import { computed, getCurrentInstance, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore } from '@/store/main'
import { useLocationStore } from '@/store/location'
import { useUserFlagsStore } from '@/store/userFlags'
import { useOnetrustStore } from '@/store/onetrust'
import { useSurveyStore } from '@/store/survey'
import { usePaywallStore } from '@/store/paywall'

import { useStatusStore } from '@/store/status'

import { useABFeatureFlag } from '@/composables/useABFeatureFlag'

import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import { isObLandingAliasPath } from '@/funnels/funnelUtils.ts'
import Jet, { Title } from '@/modules/Jet'
import { useRoute } from 'vue-router'

export function useOnetrust() {
	const proxy = getCurrentInstance().proxy
	const route = useRoute()

	const mainStore = useMainStore()
	const locationStore = useLocationStore()
	const onetrustStore = useOnetrustStore()
	const surveyStore = useSurveyStore()

	const { getCountryCode: countryCode, getIsEUCountry: isEU } = storeToRefs(mainStore)
	const { getQueryParam: getQueryParamRef } = storeToRefs(locationStore)
	const {
		getActiveGroups: onetrustActiveGroups,
		isBannerAutoClosable: isOnetrustBannerAutoClosable,
		getOptInModel: optInModel,
		bannerClosed,
	} = storeToRefs(onetrustStore)

	const getQueryParam = (key) => getQueryParamRef.value(key)

	const isOneTrustDisabled = computed(() => {
		return ENV_DEV_OR_STAGING && getQueryParam('onetrust') === false
	})

	function setOnetrustData() {
		const domainData = window.OneTrust.GetDomainData?.() || {}
		const activeGroups = window.OnetrustActiveGroups.split(',').filter(Boolean) || []
		const bannerClosedVal = window.OneTrust.IsAlertBoxClosed()
		const optInModelVal = domainData.ConsentModel?.Name === 'opt-in'

		onetrustStore.setActiveGroups(activeGroups)
		onetrustStore.setBannerClosed(bannerClosedVal)
		onetrustStore.setOptInModel(optInModelVal)
		onetrustStore.setInited(true)

		const langParam = getQueryParam('lang')
		if (langParam) {
			const lang = langParam.substring(0, 2)
			window.OneTrust.changeLanguage?.(lang)
		}

		if (!bannerClosedVal) {
			sendOnetrustAnalytics(optInModelVal)
		}

		subscribeToOnetrustEvents(optInModelVal)

		if (isOneTrustDisabled.value && !bannerClosed.value) {
			window.OneTrust.AllowAll()
		}
	}

	function sendOnetrustAnalytics(optInModelVal) {
		if (optInModelVal) {
			proxy.$analytic.logEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_POPUP' })
			Jet.track('PopupView', {}, { tracking: { title: Title.COOKIE_CONSENT_EXTENDED_POPUP, path: [] } })
		} else {
			proxy.$analytic.logEvent('PopupView', { title: 'COOKIE_CONSENT_GENERAL_POPUP' })
			Jet.track('PopupView', {}, { tracking: { title: Title.COOKIE_CONSENT_GENERAL_POPUP, path: [] } })
		}
	}

	function subscribeToOnetrustEvents(optInModelVal) {
		window.OneTrust.OnConsentChanged((event) => {
			onetrustStore.setActiveGroups(event.detail)
			onetrustStore.setBannerClosed(window.OneTrust.IsAlertBoxClosed())

			setCookiesRelatedConsents()

			proxy.$analytic.setUserProperties({
				CookieConsentActiveGroups: onetrustActiveGroups.value,
			})
		})

		document.getElementById('onetrust-consent-sdk').addEventListener(
			'click',
			(e) => {
				const clickTargetId = e.target.id || e.target.className

				if (optInModelVal) {
					if (clickTargetId === 'onetrust-accept-btn-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_CONFIRM_BUTTON' })
						Jet.track(
							'ButtonClick',
							{},
							{ tracking: { title: Title.COOKIE_CONSENT_EXTENDED_CONFIRM_BUTTON, path: [] } },
						)

						sendOnetrustDoneEvent()
					} else if (clickTargetId === 'onetrust-reject-all-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_REJECT_BUTTON' })
						Jet.track('ButtonClick', {}, { tracking: { title: Title.COOKIE_CONSENT_EXTENDED_REJECT_BUTTON, path: [] } })
					} else if (clickTargetId === 'onetrust-pc-btn-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_BUTTON' })
						Jet.track(
							'ButtonClick',
							{},
							{ tracking: { title: Title.COOKIE_CONSENT_EXTENDED_SETTINGS_BUTTON, path: [] } },
						)

						proxy.$analytic.logEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_POPUP' })
						Jet.track('PopupView', {}, { tracking: { title: Title.COOKIE_CONSENT_EXTENDED_SETTINGS_POPUP, path: [] } })
					} else if (
						clickTargetId === 'accept-recommended-btn-handler' ||
						e.target.classList.contains('ot-pc-refuse-all-handler') ||
						e.target.classList.contains('save-preference-btn-handler')
					) {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_CONFIRM_BUTTON' })
						Jet.track(
							'ButtonClick',
							{},
							{ tracking: { title: Title.COOKIE_CONSENT_EXTENDED_SETTINGS_CONFIRM_BUTTON, path: [] } },
						)

						sendOnetrustDoneEvent()
					} else if (clickTargetId === 'close-pc-btn-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_CLOSE_BUTTON' })
						Jet.track(
							'ButtonClick',
							{},
							{ tracking: { title: Title.COOKIE_CONSENT_EXTENDED_SETTINGS_CLOSE_BUTTON, path: [] } },
						)

						if (ENV_DEV_OR_STAGING) {
							const paywallStore = usePaywallStore()
							paywallStore.setCountdownFinishedAt(null)
							paywallStore.toggleCountdown(true)
							paywallStore.setRemoveDiscountAfterCountdown(null)
							useUserFlagsStore().updateLocalFlags({
								applyDiscountOnAbort: false,
								scratchCardPassed: false,
								scratchCardDiscountApplied: false,
							})
						}
					} else if (clickTargetId === 'ot-floating-button__open') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_WIDGET_BUTTON' })
						Jet.track('ButtonClick', {}, { tracking: { title: Title.COOKIE_CONSENT_WIDGET_BUTTON, path: [] } })

						proxy.$analytic.logEvent('PopupView', { title: 'COOKIE_CONSENT_EXTENDED_SETTINGS_POPUP' })
						Jet.track('PopupView', {}, { tracking: { title: Title.COOKIE_CONSENT_EXTENDED_SETTINGS_POPUP, path: [] } })
					}
				} else {
					if (clickTargetId === 'onetrust-accept-btn-handler') {
						proxy.$analytic.logEvent('ButtonClick', { title: 'COOKIE_CONSENT_GENERAL_CONFIRM_BUTTON' })
						Jet.track('ButtonClick', {}, { tracking: { title: Title.COOKIE_CONSENT_GENERAL_CONFIRM_BUTTON, path: [] } })

						sendOnetrustDoneEvent()
					}
				}
			},
			true,
		)
	}

	function sendOnetrustDoneEvent(userConsent = true) {
		setTimeout(() => {
			const bannerType = optInModel.value ? 'extended' : 'general'

			proxy.$analytic.logEvent('CookieConsentDone', {
				banner_type: bannerType,
				user_consent: Boolean(userConsent),
				cookie_type: onetrustActiveGroups.value,
			})

			Jet.track('CookieConsentDone', {
				banner_type: bannerType,
				user_consent: Boolean(userConsent),
				cookie_type: onetrustActiveGroups.value,
			})
		}, 500)
	}

	function setCookiesRelatedConsents() {
		if (countryCode.value === 'us') {
			if (onetrustActiveGroups.value.includes('targeting_cookies')) {
				surveyStore.addUserConsent('pd_marketing_purposes_us')
			} else {
				surveyStore.removeUserConsent('pd_marketing_purposes_us')
			}
		} else if (isEU.value || countryCode.value === 'uk') {
			if (onetrustActiveGroups.value.includes('targeting_cookies')) {
				surveyStore.addUserConsent('pd_marketing_purposes_eu-uk')
			} else {
				surveyStore.removeUserConsent('pd_marketing_purposes_eu-uk')
			}
		}
	}

	// web_update_cookie_gdpr_banner start
	const statusStore = useStatusStore()
	const { IS_UPDATE_COOKIE_GDPR_BANNER_EXP } = useABFeatureFlag()

	const COOKIE_BANNER_UPDATE_CLASS = 'ot-banner-updated'

	watch(
		() => [
			onetrustStore.getIsInited,
			statusStore.getResourceIsReady('absmartly'),
			isOnetrustBannerAutoClosable.value,
			IS_UPDATE_COOKIE_GDPR_BANNER_EXP.value,
		],
		() => {
			const shouldApply =
				onetrustStore.getIsInited &&
				statusStore.getResourceIsReady('absmartly') &&
				!isOnetrustBannerAutoClosable.value &&
				IS_UPDATE_COOKIE_GDPR_BANNER_EXP.value

			document.body.classList.toggle(COOKIE_BANNER_UPDATE_CLASS, shouldApply)
		},
		{ immediate: true },
	)

	// web_update_cookie_gdpr_banner end

	import('@/styles/onetrust.scss')

	// Watch $route changes
	watch(route, () => {
		if (!isOnetrustBannerAutoClosable.value) {
			return
		}

		// DEV-34114: OB-engine лендинг живёт на alias funnel-роута без meta.isLanding —
		// alias-вход тоже считается лендингом, иначе баннер авто-закрывался бы (AllowAll)
		// прямо на первом лендинг-экране. Для легаси-роутов семантика прежняя (meta).
		const isLandingRoute = route.meta.isLanding || isObLandingAliasPath(locationStore.getAppAreaConfig, route.path)

		if (!isLandingRoute || route.query.step) {
			window.OneTrust?.AllowAll?.()
			sendOnetrustDoneEvent(false)
		}
	})

	// mounted
	onMounted(() => {
		if (window.OneTrust?.GetDomainData?.().IsBannerLoaded) {
			setOnetrustData()
		} else {
			document.addEventListener('OneTrustReady', setOnetrustData, { once: true })
		}
	})
}
